/**
 * Bits shared by the three giveaway form screens (contest add/edit, past
 * contest details, winner announcement).
 */
import { format } from "date-fns";

/**
 * Shared control styling. Borders are explicit (not the lightblue fill used in
 * the older admin modals) to match the giveaway design.
 */
export const inputClass =
  "w-full p-3 rounded-lg border border-[#D1D5DB] text-sm text-grease-100 placeholder-gray-400 focus:outline-none focus:border-violet-100 focus:ring-0 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed";

/** API date string -> Date the pickers understand, or null when unset/invalid.
 * Parses UTC ISO/API date strings and converts them to the user's local timezone.
 */
export const toDate = (value?: string | number | Date | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const trimmed = value.trim();
  if (!trimmed) return null;

  // If ISO string without timezone indicator (e.g. "2026-08-21T06:15:00" or "2026-08-21 06:15:00"),
  // treat it as UTC and append 'Z' so it is properly parsed as UTC and converted to local time.
  let isoString = trimmed.replace(" ", "T");
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(isoString)) {
    isoString += "Z";
  }

  const parsed = new Date(isoString);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const toApiDate = (date: Date | null) =>
  date && !Number.isNaN(date.getTime()) ? format(date, "yyyy-MM-dd") : "";

/** Local Date -> UTC ISO-8601 string for backend API payload */
export const toApiDateTime = (date: Date | null) =>
  date && !Number.isNaN(date.getTime()) ? date.toISOString() : "";

/**
 * Format any UTC timestamp string (e.g. "2026-08-21 11:00 UTC", "2026-08-21T11:00:00Z")
 * found in backend API error messages into local date-time format (dd/MM/yyyy HH:mm).
 */
export const formatUtcStringToLocal = (rawMessage: string): string => {
  if (!rawMessage || typeof rawMessage !== "string") return rawMessage;

  return rawMessage.replace(
    /(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2}(?::\d{2})?)(?:\.\d+)?\s*(?:UTC|Z)?/gi,
    (match, datePart, timePart) => {
      // Only transform if it clearly has a UTC/Z indicator or standard datetime pattern
      if (
        !match.toUpperCase().includes("UTC") &&
        !match.endsWith("Z") &&
        !match.endsWith("z")
      ) {
        return match;
      }
      try {
        const fullTime = timePart.length === 5 ? `${timePart}:00` : timePart;
        const utcDate = new Date(`${datePart}T${fullTime}Z`);
        if (!isNaN(utcDate.getTime())) {
          return format(utcDate, "dd/MM/yyyy HH:mm");
        }
      } catch {
        // fallback to original match
      }
      return match;
    },
  );
};

/**
 * What to show for a winner. Falls back through email to the bare id so a row
 * whose user details didn't come back still reads as somebody, rather than as
 * an empty chip or a dash.
 */
export const winnerDisplayName = (winner: {
  userName?: string;
  email?: string;
  userId?: number;
}): string =>
  winner.userName?.trim() ||
  winner.email?.trim() ||
  (winner.userId ? `User ${winner.userId}` : "-");

/** 1 -> "1st", 2 -> "2nd", 13 -> "13th". */
export const ordinal = (n: number) => {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};
